import { useQuery } from "@apollo/client";
import { useCallback } from "react";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import { LogoAzureColored, Space } from "components/icons/generated";
import MetaInfoListItem from "components/MetaInfoList/Item";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import Button from "ds/components/Button";
import useAnalytics from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import { useLayoutActions } from "views/Account/Integrations/hooks/useLayoutActions";
import { useLayoutHeading } from "views/Account/Integrations/hooks/useLayoutHeading";
import { useLayoutMeta } from "views/Account/Integrations/hooks/useLayoutMeta";
import { IntegrationsOutletContext } from "views/Account/Integrations/types";

import AzureIntegrationsActionsDropdown from "../components/ActionsDropdown";
import AzureIntegrationsConsentFeedback from "../components/ConsentFeedback";
import { showAzureIntegrationDetailsDrawer } from "../components/DetailsDrawer";
import { GET_AZURE_INTEGRATION } from "./gql";
import IntegrationUsedBy from "./UsedBy";

const AzureIntegrationDetailsPage = () => {
  const { integrationId } = useParams<{ integrationId: string }>();
  const { integration } = useOutletContext<IntegrationsOutletContext>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data, loading, error } = useQuery(GET_AZURE_INTEGRATION, {
    variables: { id: integrationId },
  });
  const trackAnalytics = useAnalytics();

  const adminConsentFailedParam = searchParams.get("adminConsentFailed");
  const adminConsentFailed =
    !!adminConsentFailedParam && adminConsentFailedParam.toLowerCase() === "true";

  useTitle(`${integration.name} · ${data?.azureIntegration?.name || ""}`);

  useBreadcrumbs(
    [
      {
        title: "Integrations",
        link: "/integrations",
      },
      {
        title: integration.name,
        link: `/integrations/${integration.id}`,
      },
      {
        title: data?.azureIntegration?.name ?? "",
      },
    ],
    [data]
  );

  useLayoutHeading(data?.azureIntegration?.name ?? "");
  useLayoutMeta(
    <>
      <MetaInfoListItem
        icon={Space}
        linkText={data?.azureIntegration?.spaceDetails.name}
        href={`/spaces/${data?.azureIntegration?.spaceDetails.id}`}
      />
      <MetaInfoListItem icon={LogoAzureColored}>Azure</MetaInfoListItem>
    </>
  );

  const handleDetails = () => {
    if (data?.azureIntegration) {
      showAzureIntegrationDetailsDrawer({ integration: data.azureIntegration, trackAnalytics });
    }
  };

  const handleDeleteSuccess = useCallback(() => {
    navigate(`/integrations/${integration.id}`, { replace: true });
  }, [navigate, integration]);

  useLayoutActions(
    <>
      <Button variant="secondary" onPress={handleDetails}>
        Details
      </Button>
      {data?.azureIntegration && (
        <AzureIntegrationsActionsDropdown
          integration={data?.azureIntegration}
          trackAnalytics={trackAnalytics}
          hideSeeDetails
          onDeleteSuccess={handleDeleteSuccess}
          dotsSize="medium"
          refetchQueriesOnEdit={["GetAzureIntegration"]}
          refetchQueriesOnDelete={["SearchAzureIntegrations"]}
        />
      )}
    </>
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data) {
    return <PageLayoutSkeleton />;
  }

  if (!data?.azureIntegration) {
    return <NotFoundPage />;
  }

  return (
    <>
      <AzureIntegrationsConsentFeedback
        integration={data.azureIntegration}
        adminConsentFailed={adminConsentFailed}
        type="callout"
      />
      <IntegrationUsedBy integration={data.azureIntegration} />
    </>
  );
};

export default AzureIntegrationDetailsPage;
